import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Check,
  ClipboardCheck,
  FileText,
  GraduationCap,
  FileSignature,
  PackageCheck,
  BadgeCheck,
  type LucideIcon,
} from "lucide-react";

import {
  RECRUITMENT_STAGE_ORDER,
  type RecruitmentStage,
} from "features/courier-portal/services/courierPortalService";

interface Props {
  stageOrder: number;
}

interface StageMeta {
  titleKey: string;
  descKey: string;
  icon: LucideIcon;
}

const STAGE_META: Record<RecruitmentStage, StageMeta> = {
  DOCUMENTS: {
    titleKey: "portal.stage.documents",
    descKey: "portal.stage.descriptions.documents",
    icon: FileText,
  },
  TRAINING: {
    titleKey: "portal.stage.training",
    descKey: "portal.stage.descriptions.training",
    icon: GraduationCap,
  },
  CONTRACT: {
    titleKey: "portal.stage.contract",
    descKey: "portal.stage.descriptions.contract",
    icon: FileSignature,
  },
  EQUIPMENT: {
    titleKey: "portal.stage.equipment",
    descKey: "portal.stage.descriptions.equipment",
    icon: PackageCheck,
  },
  APPROVED: {
    titleKey: "portal.stage.approved",
    descKey: "portal.stage.descriptions.approved",
    icon: BadgeCheck,
  },
  STUCK: {
    titleKey: "portal.stage.stuck",
    descKey: "portal.stage.descriptions.documents",
    icon: ClipboardCheck,
  },
  INCOMPLETE_PROCESS: {
    titleKey: "portal.stage.incompleteProcess",
    descKey: "portal.stage.descriptions.documents",
    icon: ClipboardCheck,
  },
  DISQUALIFIED: {
    titleKey: "portal.stage.disqualified",
    descKey: "portal.stage.descriptions.documents",
    icon: ClipboardCheck,
  },
};

/**
 * Vertical recruitment stepper. Each stage renders an icon node on the
 * inline-start side and a content card with title + description on the
 * inline-end side. Inspired by PartnerOS's VerticalStepper.
 */
const RecruitmentStepper: React.FC<Props> = ({ stageOrder }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const current = stageOrder >= 0 ? stageOrder : -1;
  const isFinal =
    current >= RECRUITMENT_STAGE_ORDER.indexOf("APPROVED");

  return (
    <ol className="relative">
      <span
        aria-hidden="true"
        className="absolute top-2 bottom-2 w-px bg-gray-200"
        style={{ insetInlineStart: "23px" }}
      />
      {RECRUITMENT_STAGE_ORDER.map((s, idx) => {
        const isCompleted = isFinal || (current >= 0 && idx < current);
        const isCurrent = !isFinal && current >= 0 && idx === current;
        const meta = STAGE_META[s];
        const Icon = meta.icon;
        const title = t(meta.titleKey);
        const desc = t(meta.descKey);

        return (
          <li key={s} className="relative ps-16 pb-5 last:pb-0">
            <div className="absolute" style={{ insetInlineStart: 0, top: 0 }}>
              <div>
                <div
                  className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 ${
                    isCompleted
                      ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                      : isCurrent
                        ? "border-[#bf0035] bg-[#fdf7f9] text-[#bf0035]"
                        : "border-gray-200 bg-gray-50 text-gray-300"
                  }`}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
              </div>
            </div>

            <div
              className={`rounded-xl border px-4 py-3 ${
                isCompleted
                  ? "border-emerald-200 bg-emerald-50"
                  : isCurrent
                    ? "border-[#bf0035]/30 bg-[#bf0035]/[0.03]"
                    : "border-gray-200 bg-gray-50/60"
              }`}
            >
              <div className="flex items-start gap-2">
                <h4
                  className={`flex-1 text-sm font-bold sm:text-base ${
                    isCompleted
                      ? "text-emerald-700"
                      : isCurrent
                        ? "text-[#bf0035]"
                        : "text-gray-400"
                  }`}
                >
                  {title}
                </h4>
                {isCurrent && (
                  <span className="inline-block rounded-md border border-[#bf0035]/10 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#bf0035]">
                    {t("portal.stage.current")}
                  </span>
                )}
                {isCompleted && (
                  <span className="inline-block rounded-md border border-emerald-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                    {t("portal.stage.completed")}
                  </span>
                )}
              </div>
              <p
                className={`mt-1 text-xs leading-relaxed sm:text-sm ${
                  isCompleted
                    ? "text-gray-600"
                    : isCurrent
                      ? "text-gray-600"
                      : "text-gray-400"
                }`}
              >
                {desc}
              </p>
              {s === "TRAINING" && (isCompleted || isCurrent) ? (
                <button
                  type="button"
                  onClick={() => navigate("/portal/training")}
                  className="mt-3 inline-flex rounded-lg bg-[#bf0035] px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-[#99002a] sm:text-sm"
                >
                  {t("portal.training.cardTitle")}
                </button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default RecruitmentStepper;
